import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { ShieldCheck, Smartphone, CreditCard, ArrowLeft, Check } from "lucide-react";

export default function Checkout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => navigate("/courses")); }, [slug, navigate]);

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const startPayment = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create-order", { course_id: course.course_id });
      setOrder(data);
      if (data.demo_mode) { setBusy(false); return; } // show demo panel
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway."); setBusy(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency,
        name: "CCLPT", description: data.course_title, order_id: data.order_id,
        prefill: { name: user.name, email: user.email },
        theme: { color: "#8B0000" },
        handler: async (res) => { await verify(res); },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  const verify = async (res = {}) => {
    setBusy(true);
    try {
      await api.post("/payments/verify", {
        order_id: order?.order_id, razorpay_payment_id: res.razorpay_payment_id,
        razorpay_signature: res.razorpay_signature,
      });
      setDone(true);
      toast.success("Payment successful! You're enrolled.");
      setTimeout(() => navigate("/dashboard/courses"), 1800);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!course) return <div className="min-h-screen flex items-center justify-center font-mono text-clay text-sm bg-rice">加载中…</div>;

  return (
    <div className="min-h-screen bg-rice">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-clay hover:text-cinnabar transition-colors mb-8">
          <ArrowLeft size={16} /> Back
        </button>

        {done ? (
          <div className="border border-black/10 bg-white p-16 text-center max-w-lg mx-auto" data-testid="payment-success">
            <div className="w-16 h-16 bg-cinnabar text-rice mx-auto flex items-center justify-center mb-6"><Check size={30} /></div>
            <h1 className="font-serif text-4xl">Enrolled!</h1>
            <p className="text-clay mt-3">Redirecting you to your courses…</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <span className="label text-cinnabar">Secure Checkout</span>
              <h1 className="font-serif font-light text-4xl sm:text-5xl tracking-tighter mt-3 mb-8">Complete your enrollment</h1>

              {!order || !order.demo_mode ? (
                <div className="border border-black/10 bg-white p-8">
                  <p className="text-clay mb-6">You'll pay securely via Razorpay — UPI / QR scanner, bank cards, and net banking are all supported.</p>
                  <div className="grid grid-cols-3 gap-3 mb-8">
                    {[[Smartphone, "UPI / QR"], [CreditCard, "Cards"], [ShieldCheck, "Net Banking"]].map(([Icon, l], i) => (
                      <div key={i} className="border border-black/10 p-4 text-center">
                        <Icon className="mx-auto text-cinnabar mb-2" size={22} />
                        <p className="text-xs text-clay">{l}</p>
                      </div>
                    ))}
                  </div>
                  <button onClick={startPayment} disabled={busy} data-testid="pay-now-btn"
                    className="w-full py-4 bg-ink text-rice font-medium hover:bg-cinnabar transition-colors duration-300 disabled:opacity-60">
                    {busy ? "Processing…" : `Pay ₹${course.price_inr.toLocaleString("en-IN")}`}
                  </button>
                </div>
              ) : (
                <div className="border border-black/10 bg-white p-8" data-testid="demo-payment-panel">
                  <div className="px-4 py-2 bg-gold/20 border border-gold/40 text-xs text-ink mb-6">
                    Sandbox mode — no live Razorpay keys configured. Simulate a successful payment below.
                  </div>
                  <div className="flex flex-col items-center py-6 border border-black/10">
                    <p className="label text-clay mb-4">Scan to pay (demo)</p>
                    <img alt="QR" className="w-44 h-44 border border-black/10"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=cclpt@sbi%26am=${course.price_inr}`} />
                    <p className="font-mono text-xs text-clay mt-4">cclpt@sbi · ₹{course.price_inr.toLocaleString("en-IN")}</p>
                  </div>
                  <button onClick={() => verify()} disabled={busy} data-testid="demo-pay-btn"
                    className="w-full mt-6 py-4 bg-cinnabar text-rice font-medium hover:bg-ink transition-colors duration-300 disabled:opacity-60">
                    {busy ? "Confirming…" : "Simulate Successful Payment"}
                  </button>
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <div className="border border-black/10 bg-white">
                <img src={course.image} alt={course.title} className="w-full h-40 object-cover border-b border-black/10" />
                <div className="p-6">
                  <p className="label text-gold">{course.level} · {course.hsk}</p>
                  <h2 className="text-2xl mt-2">{course.title}</h2>
                  <p className="text-xs text-clay mt-1">{course.duration}</p>
                  <div className="mt-6 border-t border-black/10 pt-4 flex justify-between items-center">
                    <span className="text-clay">Total</span>
                    <span className="font-display font-extrabold text-2xl">₹{course.price_inr.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
